import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { PageBody, PageHeader } from "@/components/PageChrome";
import { StorageQuotaBar, humanBytes } from "@/components/StorageQuotaBar";
import { Workflow, Package, Check } from "lucide-react";
import { CURRENCIES } from "@/lib/currency";
import { useAppConfig } from "@/lib/appConfig";

export default function OrgSettingsPage() {
  const { activeOrgId, activeRole, refreshMe, switchOrg } = useAuth();
  const { defaultCurrency } = useAppConfig();
  const nav = useNavigate();
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("");
  const [busy, setBusy] = useState(false);
  const canManage = ["owner", "admin"].includes(activeRole);
  const isOwner = activeRole === "owner";
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [storage, setStorage] = useState(null);
  const [quotaMB, setQuotaMB] = useState("");
  const [savingQuota, setSavingQuota] = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  const loadAll = async () => {
    if (!activeOrgId) return;
    try {
      const [oRes, sRes] = await Promise.all([
        api.get(`/orgs/${activeOrgId}`),
        api.get(`/media/storage`),
      ]);
      setOrg(oRes.data);
      setName(oRes.data.name);
      setCurrency(oRes.data?.settings?.currency || defaultCurrency || "USD");
      setStorage(sRes.data);
      setQuotaMB(Math.round(sRes.data.quota_bytes / (1024 * 1024)));
    } catch (e) { toast.error(extractErrorMessage(e)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [activeOrgId]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.patch(`/orgs/${activeOrgId}`, { name, settings: { currency } });
      setOrg(r.data);
      toast.success("Saved");
      await refreshMe(); // updates the active currency used across the app
    } catch (err) { toast.error(extractErrorMessage(err)); }
    finally { setBusy(false); }
  };

  const deleteOrg = async () => {
    if (delConfirm !== org?.name) return;
    setDeleting(true);
    try {
      const deletedId = activeOrgId;
      await api.delete(`/orgs/${deletedId}`);
      toast.success(`"${org.name}" deleted`);
      // The active token still names the deleted org — switch to a surviving
      // one (which re-issues the JWT), or fall back to onboarding if none left.
      const me = await refreshMe();
      const remaining = (me.organizations || []).filter((o) => o.id !== deletedId);
      if (remaining.length) {
        await switchOrg(remaining[0].id);
        nav("/dashboard", { replace: true });
      } else {
        nav("/onboarding", { replace: true });
      }
    } catch (e) { toast.error(extractErrorMessage(e)); setDeleting(false); }
  };

  const saveMode = async (mode) => {
    if (savingMode || mode === (org?.mode || "service")) return;
    setSavingMode(true);
    try {
      const r = await api.patch(`/orgs/${activeOrgId}`, { mode });
      setOrg(r.data);
      toast.success(mode === "product" ? "Switched to Product (catalog) mode" : "Switched to Service (workflow) mode");
      await refreshMe(); // so the sidebar / catalog gates re-evaluate immediately
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setSavingMode(false); }
  };

  const saveQuota = async () => {
    const bytes = Math.round(Number(quotaMB) * 1024 * 1024);
    if (!bytes) return toast.error("Enter a valid quota in MB");
    setSavingQuota(true);
    try {
      await api.patch(`/orgs/${activeOrgId}/storage-quota`, {
        storage_quota_bytes: bytes,
      });
      toast.success("Storage quota updated");
      loadAll();
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setSavingQuota(false); }
  };

  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const exportWorkspace = async () => {
    setExporting(true);
    try {
      const r = await api.get("/workspace/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `ubos-workspace-${ts}.ubos`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Workspace exported");
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setExporting(false); }
  };

  const importWorkspace = async (file) => {
    if (!file) return;
    if (!window.confirm(
      "Import will REPLACE all data in this workspace with the contents of the file. " +
      "This cannot be undone.\n\nContinue?"
    )) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/workspace/import?mode=replace", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const n = Object.values(r.data?.imported || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Workspace imported (${n} items)`);
      loadAll();
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setImporting(false); }
  };

  return (
    <>
      <PageHeader
        title="Organization"
        subtitle="Settings that apply to everyone in this workspace."
        breadcrumbs={[{ label: "Settings" }, { label: "Organization" }]}
      />
      <PageBody className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">General</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={save} className="space-y-4 max-w-md">
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  disabled={!canManage} data-testid="input-org-name" />
              </div>
              <div>
                <Label>Slug</Label>
                <Input className="font-mono" value={org?.slug || ""} readOnly disabled />
              </div>
              <div>
                <Label htmlFor="org-currency">Currency</Label>
                <select
                  id="org-currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  disabled={!canManage}
                  data-testid="select-org-currency"
                  className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Used to display all prices and money fields across the workspace.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={!canManage || busy} data-testid="submit-org-save">
                  {busy ? "Saving…" : "Save changes"}
                </Button>
                {!canManage && (
                  <span className="text-xs text-muted-foreground">
                    Only owners and admins can edit organization settings.
                  </span>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card data-testid="mode-panel">
          <CardHeader>
            <CardTitle className="text-lg">Workspace mode</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choose how this workspace behaves. This only changes which tools are
              shown — your collections and data are never touched.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
              {[
                { key: "service", Icon: Workflow, title: "Service — workflow",
                  blurb: "Leads → cases/projects → phases with staff, checklists and status. Hides catalog tools." },
                { key: "product", Icon: Package, title: "Product — catalog",
                  blurb: "Inventory, categories, labels/barcodes and a media library." },
              ].map(({ key, Icon, title, blurb }) => {
                const active = (org?.mode || "service") === key;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!canManage || savingMode}
                    onClick={() => saveMode(key)}
                    data-testid={`mode-option-${key}`}
                    className={`text-left border rounded-xl p-4 transition-all disabled:opacity-60 ${
                      active ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      {active && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                          <Check className="w-3.5 h-3.5" /> Current
                        </span>
                      )}
                    </div>
                    <div className="font-medium text-[15px]">{title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{blurb}</div>
                  </button>
                );
              })}
            </div>
            {!canManage && (
              <span className="text-xs text-muted-foreground">
                Only owners and admins can change the workspace mode.
              </span>
            )}
          </CardContent>
        </Card>

        <Card data-testid="storage-panel">
          <CardHeader>
            <CardTitle className="text-lg">Storage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <StorageQuotaBar data={storage} />
            {storage && (
              <div className="text-xs text-muted-foreground font-mono">
                Max single file upload: {humanBytes(storage.max_upload_bytes)}
              </div>
            )}
            <div className="flex items-end gap-3 max-w-md">
              <div className="flex-1">
                <Label>Quota (MB)</Label>
                <Input type="number" min="100" value={quotaMB}
                  disabled={!canManage}
                  onChange={(e) => setQuotaMB(e.target.value)}
                  data-testid="input-storage-quota" />
                <p className="text-xs text-muted-foreground mt-1">
                  Range: 100 MB – 100 GB (102,400 MB)
                </p>
              </div>
              <Button onClick={saveQuota} disabled={!canManage || savingQuota}
                data-testid="submit-storage-quota">
                {savingQuota ? "Saving…" : "Update"}
              </Button>
            </div>
            <div>
              <Link to="/media" className="text-sm text-primary hover:underline"
                data-testid="link-manage-media">
                Manage in Media Library →
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="workspace-panel">
          <CardHeader>
            <CardTitle className="text-lg">Workspace backup &amp; sharing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Export your entire workspace (collections, fields, categories, tags, items and files)
              to a single <code>.ubos</code> file — for backups, or to move an updated dataset to
              another machine. Importing <b>replaces</b> everything in this workspace with the file.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={exportWorkspace} disabled={exporting} data-testid="workspace-export">
                {exporting ? "Exporting…" : "Export workspace"}
              </Button>
              {canManage && (
                <label className="inline-flex">
                  <Button
                    variant="outline"
                    disabled={importing}
                    data-testid="workspace-import"
                    onClick={(e) => e.currentTarget.nextElementSibling?.click()}
                  >
                    {importing ? "Importing…" : "Import workspace…"}
                  </Button>
                  <input
                    type="file"
                    accept=".ubos,.zip"
                    className="hidden"
                    onChange={(e) => { importWorkspace(e.target.files?.[0]); e.target.value = ""; }}
                  />
                </label>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-lg text-destructive">Danger zone</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Deleting this workspace hides it and all its collections, items and
              files from everyone. Only the workspace <b>owner</b> can do this.
            </p>
            {isOwner ? (
              <div className="max-w-md space-y-2">
                <Label className="text-sm">
                  Type <span className="font-mono font-medium text-foreground">{org?.name}</span> to confirm
                </Label>
                <Input
                  value={delConfirm}
                  onChange={(e) => setDelConfirm(e.target.value)}
                  placeholder={org?.name || ""}
                  disabled={deleting}
                  data-testid="input-delete-confirm"
                />
                <Button
                  variant="destructive"
                  disabled={deleting || delConfirm !== org?.name}
                  onClick={deleteOrg}
                  data-testid="submit-delete-org"
                >
                  {deleting ? "Deleting…" : "Delete this workspace"}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Only the workspace owner can delete it.
              </p>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  );
}
